import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import { ACTIVE, ELIGIBLE } from 'settings/_personSettings'
import reservationService from 'services/reservation'

import { Reservation } from 'models/Reservation'
import { UserData } from 'models/Auth'
import { PATIENT_HOME } from 'settings/_reactQueryConstants'

export interface Error {
  status: number
}

export interface GetPatientReservationReturn {
  reservations: Reservation[]
  isFetching: boolean
  onRefresh: () => void
  patientHasNoReservationCreatedAndPlansDontAllowReservation: boolean
  patientPlansAllowReservation?: boolean
}

export const useGetPatientReservation = (): GetPatientReservationReturn => {
  const queryClient = useQueryClient()
  const userData = useSelector((state: UserData) => state.auth.userData)
  const username = userData?.username

  const [hasErrorOnRequest, setHasErrorOnRequest] = useState(false)
  
  const patientPlansAllowReservation = userData?.person_profile?.[0]?.plans
    ?.filter((plan) => [ACTIVE, ELIGIBLE].includes(plan.status_code))
    ?.some((plan) => plan.plan_group.allows_reservation)

  const { data, isFetching, refetch } = useQuery<Reservation[] | Reservation>({
    queryKey: [PATIENT_HOME.RESERVATION_CARD, username],
    queryFn: () => reservationService.getReservations({ username, isProfessional: false }),
    enabled: !!username,
    onError: (error: Error) => {
      if (error?.status === 404) {
        queryClient.setQueriesData([PATIENT_HOME.RESERVATION_CARD, username], null)
        setHasErrorOnRequest(true)
      }
    }
  })

  const patientHasNoReservationCreatedAndPlansDontAllowReservation = hasErrorOnRequest && !patientPlansAllowReservation

  return { 
    reservations: data as Reservation[],
    isFetching,
    onRefresh: refetch,
    patientPlansAllowReservation,
    patientHasNoReservationCreatedAndPlansDontAllowReservation
  }
}